import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, orderBy, limit, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase-config';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Box, Sphere, Torus } from '@react-three/drei';

const Home = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({});
  const [stats, setStats] = useState({ courses: 0, faculty: 0 });
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [sliders, setSliders] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [notifications, setNotifications] = useState([]);
  
  const targetRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start start", "end end"]
  });

  const scale3D = useTransform(scrollYProgress, [0, 0.5, 1], [1, 1.5, 1]);
  const rotateY = useTransform(scrollYProgress, [0, 0.5, 1], [0, Math.PI, 2 * Math.PI]);

  // Helper function to get image URL from news item
  const getImageUrl = (item) => {
    if (!item) return null;
    // Try different field names that might contain the image
    return item.imageUrl || item.image || item.featuredImage || item.banner || null;
  };

  // Get fallback image
  const getFallbackImage = () => {
    return 'https://images.unsplash.com/photo-1562774053-701939374585?w=400';
  };

  // Default hero sliders
  const defaultSliders = [
    {
      id: 1,
      title: 'Welcome to Dolphin Institute',
      subtitle: 'Excellence in Medical Sciences & Technology',
      image: 'https://images.unsplash.com/photo-1516549655169-df83a0774514?w=1920&q=80'
    },
    {
      id: 2,
      title: 'State-of-the-Art Medical Education',
      subtitle: 'Advanced labs and clinical training',
      image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1920&q=80'
    },
    {
      id: 3,
      title: 'Expert Faculty & Modern Facilities',
      subtitle: 'Learn from the best in the industry',
      image: 'https://images.unsplash.com/photo-1530026405186-ed1f139313f8?w=1920&q=80'
    }
  ];

  // Features with different colors
  const features = [
    { icon: 'fa-microscope', title: 'Advanced Labs', desc: 'State-of-the-art medical laboratories with modern equipment.', color: '#0ea5e9' },
    { icon: 'fa-user-md', title: 'Expert Faculty', desc: 'Learn from experienced doctors and medical professionals.', color: '#10b981' },
    { icon: 'fa-hospital', title: 'Hospital Training', desc: 'Hands-on clinical training at partner hospitals.', color: '#f59e0b' },
    { icon: 'fa-map-marker-alt', title: 'Prime Location', desc: 'Located in the heart of the city with easy access.', color: '#8b5cf6' },
    { icon: 'fa-building', title: 'Hostel Facility', desc: 'Comfortable accommodation with modern amenities.', color: '#f43f5e' },
    { icon: 'fa-bus', title: 'Transport Facility', desc: 'Free transportation from all major points.', color: '#06b6d4' }
  ];

  // Default notifications for demo
  const defaultNotifications = [
    { id: 'demo1', title: '🎓 Admissions Open 2025-26 | Apply Now' },
    { id: 'demo2', title: '📢 Scholarship Test on 15th June - Register Now' },
    { id: 'demo3', title: '🏥 New Medical Camp at City Hospital - Free Checkup' },
    { id: 'demo4', title: '📚 Semester Exam Schedule Released' },
    { id: 'demo5', title: '🎉 Annual Medical Conference on 20th July' }
  ];

  useEffect(() => {
    const loadData = async () => {
      try {
        const settingsRef = doc(db, 'settings', 'college_info');
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists()) setSettings(settingsSnap.data());

        const coursesSnap = await getDocs(collection(db, 'courses'));
        const facultySnap = await getDocs(collection(db, 'faculty'));
        setStats({ courses: coursesSnap.size, faculty: facultySnap.size });

        try {
          const slidersQuery = query(collection(db, 'sliders'), orderBy('order', 'asc'));
          const slidersSnap = await getDocs(slidersQuery);
          const slidersData = [];
          slidersSnap.forEach(doc => slidersData.push({ id: doc.id, ...doc.data() }));
          if (slidersData.length > 0) {
            setSliders(slidersData);
          } else {
            setSliders(defaultSliders);
          }
        } catch {
          setSliders(defaultSliders);
        }

        // Load notifications
        try {
          const notificationsQuery = query(collection(db, 'notifications'), orderBy('createdAt', 'desc'), limit(15));
          const notificationsSnap = await getDocs(notificationsQuery);
          const notificationsData = [];
          notificationsSnap.forEach(doc => notificationsData.push({ id: doc.id, ...doc.data() }));
          if (notificationsData.length > 0) {
            setNotifications(notificationsData);
          } else {
            setNotifications(defaultNotifications);
          }
        } catch {
          setNotifications(defaultNotifications);
        }

        const newsQuery = query(collection(db, 'news'), orderBy('createdAt', 'desc'), limit(8));
        const newsSnap = await getDocs(newsQuery);
        const newsData = [];
        newsSnap.forEach(doc => {
          const data = { id: doc.id, ...doc.data() };
          // Log to debug image fields
          if (newsData.length === 0) {
            console.log('News data fields:', Object.keys(data));
            console.log('News data:', data);
          }
          newsData.push(data);
        });
        setNews(newsData);

        const now = new Date();
        const eventsQuery = query(collection(db, 'events'), where('eventDate', '>=', Timestamp.fromDate(now)), orderBy('eventDate', 'asc'), limit(4));
        const eventsSnap = await getDocs(eventsQuery);
        const eventsData = [];
        eventsSnap.forEach(doc => eventsData.push({ id: doc.id, ...doc.data() }));
        setEvents(eventsData);

        const testimonialSnap = await getDocs(collection(db, 'testimonials'));
        const testimonialData = [];
        testimonialSnap.forEach(doc => testimonialData.push(doc.data()));
        setTestimonials(testimonialData);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    loadData();
  }, []);

  // Auto-slide
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % (sliders.length || 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [sliders.length]);

  const currentSliders = sliders.length > 0 ? sliders : defaultSliders;
  const duplicatedNews = [...news, ...news, ...news];
  const duplicatedNotifications = [...notifications, ...notifications, ...notifications];

  // Handle notification click
  const handleNotificationClick = (id) => {
    navigate(`/notification/${id}`);
  };

  return (
    <div ref={targetRef}>
      {/* PDF Download FAB */}
      <a
        href="https://dimst.in/applyform.pdf"
        target="_blank"
        rel="noopener noreferrer"
        className="pdf-fab"
        style={{
          position: 'fixed',
          top: '50%',
          right: '20px',
          transform: 'translateY(-50%)',
          width: '58px',
          height: '58px',
          background: '#dc2626',
          borderRadius: '50%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          cursor: 'pointer',
          boxShadow: '0 6px 20px rgba(220, 38, 38, 0.5)',
          transition: 'all 0.3s ease',
          zIndex: 999,
          textDecoration: 'none',
          border: 'none'
        }}
      >
        <i className="fas fa-file-pdf" style={{ fontSize: '1.8rem', marginBottom: '2px' }}></i>
        <span style={{
          fontSize: '0.5rem',
          fontWeight: 700,
          letterSpacing: '0.3px',
          textTransform: 'uppercase',
          background: 'rgba(255,255,255,0.2)',
          padding: '1px 6px',
          borderRadius: '20px',
          marginTop: '-2px'
        }}>Form</span>
      </a>

      {/* Notification Marquee */}
      <div className="container">
        <div className="marquee-notification-container">
          <div className="marquee-static-icon">
            <i className="fas fa-bell static-flashing-icon"></i>
            <span>LATEST UPDATES</span>
          </div>
          <div className="marquee-scroll-area">
            <div className="marquee-track">
              {duplicatedNotifications.map((notif, index) => (
                <div 
                  key={`${notif.id}-${index}`} 
                  className="marquee-item"
                  onClick={() => handleNotificationClick(notif.id)}
                >
                  <i className="fas fa-bell" style={{ color: 'var(--clr-blue)' }}></i>
                  <span className="marquee-text">{notif.title || 'Announcement'}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Hero Slider Section */}
      <section className="hero-slider">
        {currentSliders.map((slide, index) => (
          <div
            key={slide.id || index}
            className={`hero-slide ${currentSlide === index ? 'active' : ''}`}
            style={{
              backgroundImage: `url(${slide.image || 'https://images.unsplash.com/photo-1516549655169-df83a0774514?w=1920&q=80'})`
            }}
          >
            <div className="hero-overlay">
              <h2 className="hero-title">{slide.title || 'Welcome'}</h2>
              <p className="hero-subtitle">{slide.subtitle || ''}</p>
            </div>
          </div>
        ))}
        
        <div className="slider-controls">
          <button onClick={() => setCurrentSlide((prev) => (prev - 1 + currentSliders.length) % currentSliders.length)}>
            <i className="fas fa-chevron-left"></i>
          </button>
          <button onClick={() => setCurrentSlide((prev) => (prev + 1) % currentSliders.length)}>
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>

        {/* 3D Overlay */}
        <div className="three-overlay">
          <Canvas camera={{ position: [0, 0, 8] }}>
            <ambientLight intensity={0.3} />
            <pointLight position={[10, 10, 10]} />
            <motion.group style={{ scale: scale3D, rotateY }}>
              <Sphere args={[1.2, 24, 24]} position={[-3, 1, 0]}>
                <meshStandardMaterial color="#0ea5e9" roughness={0.4} metalness={0.2} transparent opacity={0.3} />
              </Sphere>
              <Box args={[1.5, 1.5, 1.5]} position={[3, -1, 0]}>
                <meshStandardMaterial color="#8b5cf6" roughness={0.4} metalness={0.2} transparent opacity={0.3} />
              </Box>
            </motion.group>
          </Canvas>
        </div>
      </section>

      <div className="container">
        {/* Stats */}
        <div className="stats-grid">
          {[
            { value: stats.courses || 0, label: 'Programs', desc: 'Specialized medical courses', color: 'var(--clr-blue)', icon: 'fa-graduation-cap' },
            { value: stats.faculty || 0, label: 'Faculty', desc: 'Expert medical professionals', color: 'var(--clr-emerald)', icon: 'fa-chalkboard-user' },
            { value: '3+', label: 'Departments', desc: 'Nursing, Allied & Paramedical', color: 'var(--clr-amber)', icon: 'fa-building' },
            { value: '5000+', label: 'Alumni', desc: 'Global network', color: 'var(--clr-purple)', icon: 'fa-users' }
          ].map((stat, index) => (
            <motion.div
              key={index}
              className="stat-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              style={{ borderBottomColor: stat.color }}
            >
              <div className="stat-icon" style={{ background: stat.color }}>
                <i className={`fas ${stat.icon}`}></i>
              </div>
              <div className="stat-value" style={{ color: stat.color }}>{stat.value}</div>
              <div className="stat-label">{stat.label}</div>
              <div className="stat-desc">{stat.desc}</div>
            </motion.div>
          ))}
        </div>

        {/* Director Section */}
        <motion.div
          className="director-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="director-image">
            <img 
              src={settings.directorPhotoUrl || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&q=80'} 
              alt="Director"
            />
          </div>
          <div className="director-info">
            <h2>{settings.directorName || 'Dr. Director Name'}</h2>
            <div className="director-title">Director of Medical Sciences</div>
            <p className="director-message">{settings.directorMessage || 'Excellence in Medical Sciences & Technology Education since 2008.'}</p>
            <div className="director-stats">
              <div>
                <div className="director-stat-number">25+</div>
                <div className="director-stat-label">Years Experience</div>
              </div>
              <div>
                <div className="director-stat-number">5000+</div>
                <div className="director-stat-label">Alumni</div>
              </div>
              <div>
                <div className="director-stat-number">50+</div>
                <div className="director-stat-label">Awards</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Features */}
        <div className="features-section">
          <h2 className="section-title"><i className="fas fa-star" style={{ color: 'var(--clr-blue)' }}></i> Why Choose Dolphin Institute</h2>
          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                style={{ borderColor: `${feature.color}40`, background: `${feature.color}10` }}
                whileHover={{ y: -6, boxShadow: '0 12px 32px rgba(0, 0, 0, 0.1)' }}
              >
                <div className="feature-icon" style={{ background: feature.color }}>
                  <i className={`fas ${feature.icon}`}></i>
                </div>
                <h3 style={{ color: feature.color }}>{feature.title}</h3>
                <p>{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* News & Events */}
        <div className="news-events-grid">
          <motion.div
            className="news-section"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="section-header">
              <h3><i className="fas fa-newspaper" style={{ color: 'var(--clr-blue)' }}></i> Latest News</h3>
            </div>
            <div className="news-scroll-container">
              {news.length > 0 ? (
                <div className="news-scroll">
                  {duplicatedNews.map((item, index) => {
                    const imageUrl = getImageUrl(item) || getFallbackImage();
                    return (
                      <Link key={`${item.id}-${index}`} to={`/news/${item.id}`} className="news-item">
                        <img 
                          src={imageUrl} 
                          alt={item.title || 'News'} 
                          onError={(e) => {
                            e.target.src = getFallbackImage();
                          }}
                        />
                        <div className="news-content">
                          <div className="news-title">{item.title || 'News Update'}</div>
                          <div className="news-desc">{(item.description || '').substring(0, 80)}...</div>
                          <div className="news-date">
                            <i className="far fa-calendar-alt"></i> 
                            {item.createdAt?.toDate ? item.createdAt.toDate().toLocaleDateString() : 'Recent'}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="empty-state">
                  <i className="fas fa-newspaper"></i>
                  <p>No news available</p>
                </div>
              )}
            </div>
          </motion.div>

          <motion.div
            className="events-section"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="section-header">
              <h3><i className="fas fa-calendar-alt" style={{ color: 'var(--clr-amber)' }}></i> Upcoming Events</h3>
            </div>
            <div className="events-list">
              {events.length > 0 ? events.map(item => (
                <Link key={item.id} to={`/news/${item.id}`} className="event-item">
                  <img 
                    src={item.banner || item.image || 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=100'} 
                    alt={item.title || 'Event'} 
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=100';
                    }}
                  />
                  <div className="event-content">
                    <div className="event-title">{item.title || 'Medical Event'}</div>
                    <div className="event-desc">{(item.description || '').substring(0, 80)}...</div>
                    <div className="event-date">
                      <i className="far fa-calendar-alt"></i> 
                      {item.eventDate?.toDate ? item.eventDate.toDate().toLocaleDateString() : 'Coming soon'}
                    </div>
                  </div>
                </Link>
              )) : (
                <div className="empty-state">
                  <i className="fas fa-calendar-alt"></i>
                  <p>No upcoming events</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Testimonials */}
        <div className="testimonials-section">
          <h2 className="section-title"><i className="fas fa-star" style={{ color: 'var(--clr-rose)' }}></i> Student Testimonials</h2>
          <div className="testimonials-grid">
            {testimonials.slice(0, 3).map((t, index) => (
              <motion.div
                key={index}
                className="testimonial-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5, boxShadow: '0 12px 32px rgba(0, 0, 0, 0.1)' }}
              >
                <div className="testimonial-stars">{'★'.repeat(t.rating || 5)}</div>
                <p className="testimonial-text">"{t.review || 'Excellent institution!'}"</p>
                <div className="testimonial-author">
                  <img 
                    src={t.image || 'https://randomuser.me/api/portraits/men/32.jpg'} 
                    alt={t.studentName || 'Student'} 
                    onError={(e) => {
                      e.target.src = 'https://randomuser.me/api/portraits/men/32.jpg';
                    }}
                  />
                  <div>
                    <h4>{t.studentName || 'Anonymous'}</h4>
                    <p>{t.course || 'Medical Student'}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Contact CTA */}
        <motion.div
          className="contact-cta"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2>Get In Touch</h2>
          <p>Have questions about our programs? We're here to help. Contact us today.</p>
          <Link to="/contact" className="contact-btn">
            <i className="fas fa-envelope"></i> Contact Us
          </Link>
        </motion.div>
      </div>

      {/* Add CSS for responsiveness */}
      <style>{`
        /* Notification Marquee Styles */
        .marquee-notification-container {
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(20px) saturate(140%);
          border: 1px solid rgba(255, 255, 255, 0.6);
          border-radius: 40px;
          margin: 12px 0 8px 0;
          padding: 0;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
          overflow: hidden;
          display: flex;
          align-items: center;
        }

        .marquee-static-icon {
          flex-shrink: 0;
          background: linear-gradient(135deg, var(--clr-blue), #0284c7);
          padding: 8px 20px;
          border-radius: 40px 0 0 40px;
          color: white;
          font-size: 1.1rem;
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 700;
        }

        .static-flashing-icon {
          animation: staticPulseFlash 1s infinite;
        }

        @keyframes staticPulseFlash {
          0%, 100% { opacity: 0.7; text-shadow: 0 0 0px rgba(255,255,255,0); }
          50% { opacity: 1; text-shadow: 0 0 8px rgba(255,255,255,0.8); }
        }

        .marquee-scroll-area {
          flex: 1;
          overflow: hidden;
          padding: 6px 0;
        }

        .marquee-track {
          display: flex;
          align-items: center;
          white-space: nowrap;
          will-change: transform;
          gap: 28px;
          animation: scrollMarquee 35s linear infinite;
        }

        .marquee-track:hover {
          animation-play-state: paused;
        }

        @keyframes scrollMarquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }

        .marquee-item {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          background: rgba(255, 255, 255, 0.6);
          padding: 5px 18px;
          border-radius: 30px;
          border: 1px solid rgba(255, 255, 255, 0.7);
          transition: all 0.2s ease;
          cursor: pointer;
        }

        .marquee-item:hover {
          background: rgba(255, 255, 255, 0.9);
          transform: scale(1.02);
          border-color: var(--clr-blue);
        }

        .marquee-text {
          font-weight: 600;
          font-size: 0.8rem;
          color: var(--text-primary);
        }

        /* Responsive styles for marquee */
        @media (max-width: 768px) {
          .marquee-static-icon {
            padding: 6px 12px;
            font-size: 0.8rem;
          }
          .marquee-static-icon span {
            display: none;
          }
          .marquee-text {
            font-size: 0.7rem;
          }
          .marquee-item {
            padding: 4px 12px;
          }
        }

        /* Hero Slider Styles */
        .hero-slider {
          position: relative;
          height: 85vh;
          min-height: 600px;
          width: 100%;
          overflow: hidden;
        }

        .hero-slide {
          position: absolute;
          width: 100%;
          height: 100%;
          opacity: 0;
          transition: opacity 0.8s ease;
          background-size: cover;
          background-position: center;
        }

        .hero-slide.active {
          opacity: 1;
        }

        .hero-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(0deg, rgba(15,23,42,0.85) 0%, transparent 100%);
          padding: 2.5rem;
          color: white;
        }

        .hero-title {
          font-family: 'Playfair Display', serif;
          font-size: 2.2rem;
          font-weight: 700;
        }

        .hero-subtitle {
          font-size: 1rem;
          opacity: 0.9;
        }

        .slider-controls {
          position: absolute;
          bottom: 20px;
          right: 20px;
          display: flex;
          gap: 10px;
          z-index: 10;
        }

        .slider-controls button {
          background: rgba(0,0,0,0.5);
          border: none;
          width: 42px;
          height: 42px;
          border-radius: 50%;
          cursor: pointer;
          color: white;
          transition: all 0.3s ease;
        }

        .three-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 2;
          opacity: 0.15;
        }

        /* Stats */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin: 40px 0;
        }

        .stat-card {
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(20px) saturate(140%);
          border-radius: 16px;
          padding: 20px 16px;
          text-align: center;
          transition: all 0.3s ease;
          border-bottom: 3px solid var(--clr-blue);
        }

        .stat-icon {
          width: 50px;
          height: 50px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 10px;
          font-size: 1.3rem;
          color: white;
        }

        .stat-value {
          font-family: 'Playfair Display', serif;
          font-size: 2rem;
          font-weight: 800;
        }

        .stat-label {
          font-size: 0.75rem;
          font-weight: 700;
          margin: 8px 0 5px;
          text-transform: uppercase;
        }

        .stat-desc {
          font-size: 0.7rem;
          color: var(--text-secondary);
        }

        /* Director */
        .director-section {
          display: grid;
          grid-template-columns: 0.9fr 1.1fr;
          gap: 30px;
          background: rgba(255, 255, 255, 0.45);
          backdrop-filter: blur(20px) saturate(140%);
          border-radius: 24px;
          border: 1px solid var(--clr-blue-border);
          overflow: hidden;
          margin: 40px 0;
        }

        .director-image {
          position: relative;
          height: 100%;
          min-height: 350px;
          overflow: hidden;
        }

        .director-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s;
        }

        .director-info {
          padding: 35px 40px;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .director-info h2 {
          font-family: 'Playfair Display', serif;
          font-size: 1.8rem;
          margin-bottom: 10px;
        }

        .director-title {
          color: var(--clr-blue);
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 15px;
          letter-spacing: 1px;
        }

        .director-message {
          color: var(--text-secondary);
          line-height: 1.6;
          margin-bottom: 20px;
          font-size: 0.9rem;
        }

        .director-stats {
          display: flex;
          gap: 25px;
          margin-top: 10px;
        }

        .director-stat-number {
          font-size: 1.5rem;
          font-weight: 800;
          color: var(--clr-blue);
          font-family: 'Playfair Display', serif;
        }

        .director-stat-label {
          font-size: 0.7rem;
          color: var(--text-secondary);
        }

        /* Features */
        .features-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          margin: 40px 0;
        }

        .feature-card {
          backdrop-filter: blur(20px) saturate(140%);
          border-radius: 16px;
          padding: 28px 20px;
          text-align: center;
          transition: all 0.3s ease;
          border: 1px solid;
        }

        .feature-icon {
          width: 65px;
          height: 65px;
          border-radius: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 18px;
          color: white;
          font-size: 1.6rem;
        }

        .feature-card h3 {
          font-family: 'Playfair Display', serif;
          font-size: 1.1rem;
          margin-bottom: 10px;
        }

        .feature-card p {
          font-size: 0.85rem;
          color: var(--text-secondary);
          line-height: 1.5;
        }

        /* News & Events */
        .news-events-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 28px;
          margin: 40px 0;
        }

        .news-section, .events-section {
          background: rgba(255, 255, 255, 0.45);
          backdrop-filter: blur(20px) saturate(140%);
          border-radius: 24px;
          overflow: hidden;
          border: 1px solid var(--clr-blue-border);
        }

        .section-header {
          padding: 20px 24px;
          background: linear-gradient(135deg, rgba(14,165,233,0.1), rgba(14,165,233,0.05));
          border-bottom: 1px solid var(--clr-blue-border);
        }

        .section-header h3 {
          font-family: 'Playfair Display', serif;
          font-size: 1.3rem;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .news-scroll-container {
          padding: 16px;
          height: 500px;
          overflow: hidden;
          position: relative;
        }

        .news-scroll {
          animation: scrollNews 25s linear infinite;
        }

        .news-scroll:hover {
          animation-play-state: paused;
        }

        @keyframes scrollNews {
          0% { transform: translateY(0); }
          100% { transform: translateY(-50%); }
        }

        .news-item, .event-item {
          display: flex;
          gap: 15px;
          padding: 18px;
          background: rgba(255, 255, 255, 0.5);
          border-radius: 16px;
          margin-bottom: 15px;
          transition: all 0.3s ease;
          cursor: pointer;
          border: 1px solid transparent;
          text-decoration: none;
          color: inherit;
        }

        .news-item:hover, .event-item:hover {
          background: rgba(255, 255, 255, 0.8);
          border-color: var(--clr-blue);
          transform: translateX(5px);
        }

        .news-item img, .event-item img {
          width: 85px;
          height: 85px;
          border-radius: 16px;
          object-fit: cover;
          flex-shrink: 0;
        }

        .news-content, .event-content {
          flex: 1;
          min-width: 0;
        }

        .news-title, .event-title {
          font-weight: 800;
          font-size: 1rem;
          margin-bottom: 6px;
        }

        .news-desc, .event-desc {
          font-size: 0.8rem;
          color: var(--text-secondary);
          line-height: 1.4;
          margin-bottom: 6px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .news-date, .event-date {
          font-size: 0.7rem;
          color: var(--clr-blue);
          font-weight: 600;
        }

        .events-list {
          padding: 16px;
          max-height: 500px;
          overflow-y: auto;
        }

        .empty-state {
          text-align: center;
          padding: 30px;
          color: var(--text-secondary);
        }

        .empty-state i {
          font-size: 2rem;
          display: block;
          margin-bottom: 10px;
        }

        /* Testimonials */
        .testimonials-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          margin: 40px 0;
        }

        .testimonial-card {
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(20px) saturate(140%);
          border-radius: 24px;
          padding: 24px;
          border: 1px solid var(--clr-rose-border);
          transition: all 0.3s ease;
        }

        .testimonial-stars {
          color: var(--clr-amber);
          margin-bottom: 12px;
        }

        .testimonial-text {
          font-style: italic;
          font-size: 0.85rem;
          line-height: 1.6;
          color: var(--text-secondary);
          margin-bottom: 15px;
        }

        .testimonial-author {
          display: flex;
          align-items: center;
          gap: 12px;
          padding-top: 12px;
          border-top: 1px solid rgba(0,0,0,0.05);
        }

        .testimonial-author img {
          width: 45px;
          height: 45px;
          border-radius: 50%;
          object-fit: cover;
          border: 2px solid var(--clr-blue);
        }

        .testimonial-author h4 {
          font-size: 0.9rem;
        }

        .testimonial-author p {
          font-size: 0.7rem;
          color: var(--text-secondary);
        }

        /* Contact CTA */
        .contact-cta {
          background: rgba(255, 255, 255, 0.45);
          backdrop-filter: blur(20px) saturate(140%);
          border-radius: 24px;
          padding: 40px;
          text-align: center;
          margin: 40px 0 60px;
          border: 1px solid var(--clr-blue-border);
        }

        .contact-cta h2 {
          font-family: 'Playfair Display', serif;
          font-size: 2rem;
          margin-bottom: 15px;
        }

        .contact-cta p {
          color: var(--text-secondary);
          max-width: 600px;
          margin: 0 auto 25px;
        }

        .contact-btn {
          display: inline-block;
          padding: 14px 40px;
          background: linear-gradient(135deg, var(--clr-blue), #0284c7);
          border-radius: 40px;
          color: white;
          font-weight: 700;
          transition: all 0.3s ease;
          text-decoration: none;
        }

        .contact-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(14,165,233,0.3);
        }

        /* PDF FAB Responsive */
        @media (max-width: 768px) {
          .pdf-fab {
            width: 46px !important;
            height: 46px !important;
            right: 10px !important;
          }
          .pdf-fab i {
            font-size: 1.3rem !important;
          }
          .pdf-fab span {
            display: none !important;
          }
        }

        /* Mobile Responsive */
        @media (max-width: 768px) {
          .hero-slider {
            height: 50vh;
            min-height: 300px;
          }

          .hero-title {
            font-size: 1.5rem;
          }

          .hero-subtitle {
            font-size: 0.85rem;
          }

          .hero-overlay {
            padding: 1.5rem;
          }

          .slider-controls button {
            width: 32px;
            height: 32px;
            font-size: 0.8rem;
          }

          .stats-grid {
            grid-template-columns: 1fr 1fr;
            gap: 15px;
            margin: 30px 0;
          }

          .stat-card {
            padding: 16px 12px;
          }

          .stat-icon {
            width: 40px;
            height: 40px;
            font-size: 1rem;
          }

          .stat-value {
            font-size: 1.5rem;
          }

          .director-section {
            grid-template-columns: 1fr;
            gap: 0;
            margin: 30px 0;
          }

          .director-image {
            min-height: 250px;
          }

          .director-info {
            padding: 24px;
          }

          .director-info h2 {
            font-size: 1.5rem;
          }

          .director-stats {
            gap: 15px;
          }

          .director-stat-number {
            font-size: 1.2rem;
          }

          .features-grid {
            grid-template-columns: 1fr;
            gap: 16px;
            margin: 30px 0;
          }

          .feature-card {
            padding: 20px 16px;
          }

          .feature-icon {
            width: 50px;
            height: 50px;
            font-size: 1.3rem;
          }

          .feature-card h3 {
            font-size: 1rem;
          }

          .news-events-grid {
            grid-template-columns: 1fr;
            gap: 20px;
            margin: 30px 0;
          }

          .section-header {
            padding: 16px 20px;
          }

          .section-header h3 {
            font-size: 1.1rem;
          }

          .news-scroll-container {
            height: 400px;
            padding: 12px;
          }

          .news-item, .event-item {
            padding: 14px;
            gap: 12px;
          }

          .news-item img, .event-item img {
            width: 60px;
            height: 60px;
          }

          .news-title, .event-title {
            font-size: 0.85rem;
          }

          .news-desc, .event-desc {
            font-size: 0.7rem;
            -webkit-line-clamp: 2;
          }

          .events-list {
            padding: 12px;
            max-height: 400px;
          }

          .testimonials-grid {
            grid-template-columns: 1fr;
            gap: 16px;
            margin: 30px 0;
          }

          .testimonial-card {
            padding: 20px;
          }

          .testimonial-text {
            font-size: 0.8rem;
          }

          .testimonial-author img {
            width: 40px;
            height: 40px;
          }

          .contact-cta {
            padding: 30px 20px;
            margin: 30px 0 40px;
          }

          .contact-cta h2 {
            font-size: 1.5rem;
          }

          .contact-btn {
            padding: 12px 30px;
            font-size: 0.9rem;
          }

          .three-overlay {
            display: none;
          }
        }

        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: 1fr 1fr;
            gap: 10px;
          }

          .stat-card {
            padding: 12px 10px;
          }

          .stat-icon {
            width: 35px;
            height: 35px;
            font-size: 0.85rem;
          }

          .stat-value {
            font-size: 1.2rem;
          }

          .stat-label {
            font-size: 0.65rem;
          }

          .stat-desc {
            font-size: 0.55rem;
          }

          .director-stats {
            flex-wrap: wrap;
          }
        }
      `}</style>
    </div>
  );
};

export default Home;